import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { Trans, useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';

import * as Alert from '@common/services/alert';
import Bar from '@common/components/bar';
import { Button } from '@common/components/button';
import Placeholder from '@common/components/placeholder';
import AddModuleForm from '../../forms/add-module';
import Section from '../section';
import ScrollArea from './scroll-area';
import Confirm from '@common/components/confirm-button';

import dndManager from '@common/utils/dnd-manager';
import { Api } from '@common/services/api';
import { useAppSelector } from '@common/hooks';
import createTempId from '@common/utils/create-temp-id';
import { CourseWithSections, Screen, Section as SectionType } from '@modules/learning/types/objects';
import { getCurrentOrgId } from '@modules/organisation/selectors/organisation';

import updateCourse from '@modules/learning/actions/update-course';
import updateModule from '@modules/learning/actions/update-module';
import removeModule from '@modules/learning/actions/remove-module';
import { duplicateModule } from '@modules/learning/actions';

type CourseContentProps = {
  course: CourseWithSections;
};

type OrderType = Omit<SectionType, 'modules'>;

// const updateOrder = (id: string, index: number, order: string[]) => {
//   return R.insert(index, id, R.reject(R.equals(id), order));
// };

const mapSectionsForPersist = (sections: OrderType[]) => {
  return sections.map((section, index) => ({
    id: section.local || section.transparent ? undefined : section.id,
    index,
    name: section.name || null,
    transparent: section.module_ids.length > 1 ? false : section.transparent || false,
    modules: section.module_ids,
  }));
};

const sanitizeSectionTransparency = (section: OrderType) => {
  if (section.module_ids.length > 1 && section.transparent) {
    return {
      ...section,
      transparent: false,
    };
  }
  return section;
};

const getOrder = (sections: SectionType[]) => {
  return R.map((section) => {
    const sanitizedSection = R.omit(['modules'], section) as OrderType;
    return sanitizeSectionTransparency(sanitizedSection);
  }, sections);
};

const CourseContent = ({ course }: CourseContentProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const organisationId = useAppSelector(getCurrentOrgId);
  const [order, setOrder] = useState<OrderType[]>([]);
  const [isCreatingSection, setIsCreatingSection] = useState(false);
  const [moduleCascadePublishId, setModuleCascadePublishId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const lastMoveRef = React.useRef<{ sourceId: string; targetId: string; sectionId: string } | null>(null);
  const previousOrderRef = React.useRef<OrderType[] | null>(null);

  const save = async (newOrderToSave: OrderType[] | null = null) => {
    const orderToPersist = mapSectionsForPersist(newOrderToSave || order);
    const { item } = await dispatch(updateCourse(course.id, {
      sections: orderToPersist,
    })) as unknown as { item: CourseWithSections };
    setOrder(getOrder(item.sections));
  };

  useEffect(() => {
    if (!course) return;
    const hasTransparentMismatch = course.sections.some((section) => {
      const moduleIds = section.module_ids || section.modules?.map((mod) => mod.id) || [];
      return moduleIds.length > 1 && section.transparent;
    });
    if (order.length > 0) return;
    const normalizedOrder = getOrder(course.sections);
    setOrder(normalizedOrder);
    if (!hasTransparentMismatch) return;
    const persistInitialOrder = async () => {
      try {
        const orderToPersist = mapSectionsForPersist(normalizedOrder);
        const { item } = await dispatch(updateCourse(course.id, {
          sections: orderToPersist,
        })) as unknown as { item: CourseWithSections };
        setOrder(getOrder(item.sections));
      } catch (error) {
        Alert.error(t('learning:course_cant_save_changes'));
      }
    };
    persistInitialOrder();
  }, [course, dispatch, order.length, t]);

  const lookUp = {
    sections: R.indexBy(R.prop('id'), R.map(R.omit(['modules']), course ? order : [])),
    modules: R.indexBy(R.prop('id'), R.flatten(R.pluck('modules', course ? course.sections : []))),
  };

  const items = order.map((values) => {
    const { module_ids: ids } = values;
    const section = lookUp.sections[values.id] || values;

    // Remove duplicate module IDs (safety check)
    const uniqueIds = R.uniq(ids);

    // Filter out any undefined modules (modules that were deleted but still in module_ids)
    const modules = uniqueIds
      .map((moduleId) => {
        const module = lookUp.modules[moduleId];
        if (!module) return null;
        return R.assoc('section_id', values.id, module);
      })
      .filter(Boolean);
    return R.assoc('modules', modules, section);
  });

  const handleOpenModule = (id: string) => {
    history.push(`/admin/learning/courses/${course.id}/modules/${id}`);
  };

  if (items.length === 0) {
    return (
      <Placeholder
        image="/static/images/modules-placeholder.svg"
        title={t('learning:course_placeholder_title')}
        description={t('learning:course_placeholder_description')}
        action={(
          <AddModuleForm
            course={course}
            onOpenCourse={handleOpenModule}
          >
            <Button type="primary" iconRight="add">
              {t('learning:course_new_module')}
            </Button>
          </AddModuleForm>
        )}
      />
    );
  }

  // Store order before drag starts for module moves within section
  const storePreviousOrder = () => {
    if (!previousOrderRef.current) {
      previousOrderRef.current = [...order];
    }
  };

  const saveOrder = async (newOrderToSave: OrderType[] | null = null) => {
    // If there's a stored previous order (from module drag within section), use rollback-aware save
    const storedPreviousOrder = previousOrderRef.current;
    if (storedPreviousOrder) {
      setIsReordering(true);
      try {
        // Reset move tracking when saving
        lastMoveRef.current = null;
        await save(newOrderToSave);
        Alert.success(t('learning:course_changes_saved'));
      } catch (error) {
        // Rollback to previous order on error
        setOrder(storedPreviousOrder);
        Alert.error(t('learning:course_cant_save_changes'));
        throw error;
      } finally {
        setIsReordering(false);
        previousOrderRef.current = null;
      }
      return;
    }

    // Standard save without rollback (for non-drag operations)
    try {
      // Reset move tracking when saving
      lastMoveRef.current = null;
      await save(newOrderToSave);
      Alert.success(t('learning:course_changes_saved'));
    } catch (error) {
      Alert.error(t('learning:course_cant_save_changes'));
      throw error;
    }
  };

  // Optimistic save with rollback support - for section and cross-section module moves
  const saveOrderOptimistic = async (newOrder: OrderType[], previousOrder: OrderType[]) => {
    // Store previous order for potential rollback
    previousOrderRef.current = previousOrder;
    setIsReordering(true);

    // Optimistically update UI immediately
    setOrder(newOrder);

    try {
      // Reset move tracking when saving
      lastMoveRef.current = null;
      await save(newOrder);
      Alert.success(t('learning:course_changes_saved'));
    } catch (error) {
      // Rollback to previous order on error
      setOrder(previousOrder);
      Alert.error(t('learning:course_cant_save_changes'));
      throw error;
    } finally {
      setIsReordering(false);
      previousOrderRef.current = null;
    }
  };

  const onSectionNameSave = async (section: SectionType, newName: string) => {
    const newOrder = order.map((orderSection) => {
      return {
        ...orderSection,
        name: orderSection.id === section.id ? newName : orderSection.name
      };
    });
    try {
      await save(newOrder);
      Alert.success(t('learning:course_changes_saved'));
    } catch (error) {
      Alert.error(t('learning:course_cant_save_changes'));
      throw error;
    }
  };

  const handleMoveModule = (sourceId: string, targetId: string, sectionId: string) => {
    // Prevent reordering while another operation is in progress
    if (isReordering) return;

    // Prevent duplicate updates for the exact same move operation
    const lastMove = lastMoveRef.current;
    if (lastMove && lastMove.sourceId === sourceId && lastMove.targetId === targetId && lastMove.sectionId === sectionId) {
      return;
    }

    // Store previous order for rollback (only on first move of a drag operation)
    storePreviousOrder();

    setOrder((prevOrder) => {
      // Use a more defensive approach - ensure we don't create duplicates
      const result = prevOrder.map((section) => {
        if (section.id !== sectionId) return section;
        const moduleIds = [...section.module_ids]; // Create a copy to avoid mutations

        const sourceIndex = R.indexOf(sourceId, moduleIds);
        const targetIndex = R.indexOf(targetId, moduleIds);

        // Prevent unnecessary updates
        if (sourceIndex === -1) return section;

        // If targetId is not found, it means we're dropping at the bottom
        // In this case, we want to move the source to the end
        if (targetIndex === -1) {
          // Remove the source first
          const withoutSource = R.reject(R.equals(sourceId), moduleIds);
          // Append to the end
          const newOrder = R.append(sourceId, withoutSource);

          // Only update if the order actually changed
          if (R.equals(newOrder, section.module_ids)) {
            return section;
          }

          return R.assoc('module_ids', newOrder, section);
        }

        // For same-section drags: pure splice operation
        // Follow the correct algorithm:
        // 1. Remove source at sourceIndex
        // 2. Calculate finalIndex in the modified array
        // 3. Insert at finalIndex
        // targetIndex is the destination position in the original array

        // Check if already in the correct position
        if (sourceIndex === targetIndex) {
          return section;
        }

        // Step 1: Remove the source first (this is critical - must happen before calculating final index)
        // Use index-based removal to ensure exactly one item is removed
        const withoutSource = [
          ...moduleIds.slice(0, sourceIndex),
          ...moduleIds.slice(sourceIndex + 1),
        ];

        // Verify removal was successful (safety check)
        if (withoutSource.length !== moduleIds.length - 1) {
          console.warn('Source removal failed, using reject as fallback');
          const fallbackWithoutSource = R.reject(R.equals(sourceId), moduleIds);
          if (fallbackWithoutSource.length === moduleIds.length) {
            // Source not found, cannot proceed
            return section;
          }
          // Use fallback but verify no duplicates
          const sourceCount = fallbackWithoutSource.filter((id) => id === sourceId).length;
          if (sourceCount > 0) {
            console.warn('Source still present after removal, aborting');
            return section;
          }
          // Continue with fallback
        }

        // Step 2: Calculate finalIndex in the modified array (after removal)
        // targetIndex is the position of the target item in the ORIGINAL array
        // NO SPECIAL CASES: destinationIndex === modules.length is treated exactly like any other index
        // Standard reorder logic: calculate destination based on relative positions, then adjust after removal
        const isDraggingDown = sourceIndex < targetIndex;

        // Calculate destinationIndex in the ORIGINAL array (before removal)
        // When hovering over an item, we want to insert AFTER it (not at its position)
        // So destinationIndex = targetIndex + 1 in the original array
        // Exception: if dragging up, we insert at targetIndex (before the target)
        const destinationIndex = isDraggingDown ? targetIndex + 1 : targetIndex;

        // Calculate finalIndex in the MODIFIED array (after removal)
        // After removing source, if sourceIndex < destinationIndex, destination shifts down by 1
        let finalIndex: number;
        if (sourceIndex < destinationIndex) {
          // Source was before destination, so destination shifts down by 1 after removal
          finalIndex = destinationIndex - 1;
        } else {
          // Source was at or after destination, so destination stays the same
          finalIndex = destinationIndex;
        }

        // Step 3: Insert at finalIndex using standard splice operation
        // R.insert(index, item, array) is equivalent to array.splice(index, 0, item)
        // This works for all indices including index === array.length (appends)
        // NO SPECIAL APPEND LOGIC - treat length as a normal index, no push/append helpers
        const newOrder = R.insert(finalIndex, sourceId, withoutSource);

        // Double-check: ensure sourceId appears only once
        const sourceCount = newOrder.filter((id) => id === sourceId).length;
        if (sourceCount !== 1) {
          console.warn('Duplicate detected in handleMoveModule, preventing update');
          return section;
        }

        // Only update if the order actually changed
        if (R.equals(newOrder, section.module_ids)) {
          return section;
        }

        // Update order of modules inside section
        return R.assoc('module_ids', newOrder, section);
      });

      // Track this move to prevent duplicates (update ref synchronously)
      if (!R.equals(result, prevOrder)) {
        lastMoveRef.current = { sourceId, targetId, sectionId };
      }

      return result;
    });
  };

  const handleMoveSection = (sourceId: string, targetId: string | null) => {
    // Prevent reordering while another operation is in progress
    if (isReordering) return;
    if (!sourceId) return;

    const previousOrder = [...order];
    const currentValue = R.find(R.propEq('id', sourceId), order);
    if (!currentValue) return;
    const cleanOrder = R.reject(R.propEq('id', sourceId), order);

    // targetId represents the section AFTER which we want to insert
    // targetId === null means insert at the top (index 0)
    // targetId === undefined/empty means insert at the bottom (index length)
    let newIndex: number;
    if (targetId === null) {
      // Dropping at the top - insert at index 0
      newIndex = 0;
    } else if (!targetId) {
      // Dropping at the very bottom - insert at the end
      newIndex = cleanOrder.length;
    } else {
      // Find the target index in the clean order (without the source)
      const targetIndex = R.findIndex(R.propEq('id', targetId), cleanOrder);
      if (targetIndex >= 0) {
        // Insert AFTER the target section (add +1)
        newIndex = targetIndex + 1;
      } else {
        // Target not found, insert at the bottom as fallback
        newIndex = cleanOrder.length;
      }
    }

    const newOrder = R.insert(newIndex, currentValue, cleanOrder) as OrderType[];

    // Skip if order didn't change
    if (R.equals(newOrder, order)) return;

    // Use optimistic save with rollback support
    saveOrderOptimistic(newOrder, previousOrder);
  };

  const handleCreateNewSection = (afterSection: string, id: string) => {
    // afterSection is the section AFTER which we want to create the new section
    // If afterSection is undefined/empty, create at the beginning (index 0)
    let newIndex: number;
    if (!afterSection) {
      newIndex = 0;
    } else {
      const afterIndex = R.findIndex(R.propEq('id', afterSection), order);
      if (afterIndex >= 0) {
        // Insert AFTER the section (add +1)
        newIndex = afterIndex + 1;
      } else {
        // Section not found, append to end
        newIndex = order.length;
      }
    }

    const newSection: OrderType = {
      id: id || createTempId(),
      index: newIndex,
      name: null,
      local: true,
      transparent: true,
      module_ids: [],
    };
    setOrder(R.insert(newIndex, newSection, order));
    return newSection;
  };

  const handleMoveModuleToSection = (sourceId: string, targetId: string, currentSectionId: string, newSection?: OrderType) => {
    // Prevent reordering while another operation is in progress
    if (isReordering) return;

    const previousOrder = [...order];

    // Check if target section exists in current order
    const targetSectionExists = order.some((section) => section.id === targetId);

    // If newSection is provided and target doesn't exist, add it to the order first
    // This handles the case where createNewSection was called but state hasn't updated yet
    let workingOrder = [...order];
    if (newSection && !targetSectionExists) {
      // Find the index where the new section should be inserted
      // newSection should have been created with the correct index
      const insertIndex = newSection.index !== undefined && newSection.index <= workingOrder.length
        ? newSection.index
        : workingOrder.length;
      // Double-check we're not adding a duplicate
      const alreadyExists = workingOrder.some((section) => section.id === targetId);
      if (!alreadyExists) {
        workingOrder = R.insert(insertIndex, newSection, workingOrder);
      }
    }

    // Ensure target section exists (either from order or from newSection)
    const finalTargetExists = workingOrder.some((section) => section.id === targetId);
    if (!finalTargetExists) {
      return;
    }

    const newOrder = workingOrder.map((section) => {
      if (section.id !== targetId && section.id !== currentSectionId) return section;
      // Remove module from old section
      if (section.id === currentSectionId) {
        return R.assoc('module_ids', R.reject(R.equals(sourceId), section.module_ids), section);
      }
      // Add module to target section (avoid duplicates)
      const moduleIds = section.module_ids.includes(sourceId)
        ? section.module_ids
        : R.append(sourceId, section.module_ids);
      return R.assoc('module_ids', moduleIds, section);
    }).filter((section) => (!section.transparent && !section.local) || section.module_ids.length > 0);

    // Skip if order didn't change
    if (R.equals(newOrder, order)) return;

    // Use optimistic save with rollback support
    saveOrderOptimistic(newOrder, previousOrder);
  };

  const handleAddNewSection = async () => {
    const section: SectionType = {
      id: createTempId(),
      index: order.length,
      name: t('learning:course_new_section_name'),
      local: true,
      transparent: false,
      module_ids: [],
      modules: [],
    };
    const newOrder = R.append(section, order);

    try {
      setIsCreatingSection(true);
      await save(newOrder);
      Alert.success(t('learning:course_changes_saved'));
    } catch (error) {
      Alert.error(t('learning:course_cant_save_changes'));
      throw error;
    } finally {
      setIsCreatingSection(false);
    }
  };

  const handleRemoveSection = async (id: string) => {
    const section = R.find(R.propEq('id', id), order);
    if (!section) return;

    const currentIndex = R.findIndex(R.propEq('id', id), order);
    const cleanOrder = R.reject(R.propEq('id', id), order);

    // If section is not transparent, create individual transparent sections for each module
    const newSections: OrderType[] = !section.transparent ? section.module_ids.map((moduleId, moduleIndex) => ({
      id: createTempId(),
      index: currentIndex + moduleIndex,
      name: null,
      local: true,
      transparent: true,
      module_ids: [moduleId],
    })) : [];

    // Insert new sections at the position where the removed section was
    const newOrder = newSections.length > 0
      ? [
          ...R.take(currentIndex, cleanOrder),
          ...newSections,
          ...R.drop(currentIndex, cleanOrder),
        ]
      : cleanOrder;

    try {
      await save(newOrder);
      setOrder(newOrder);
      Alert.success(t('learning:course_changes_saved'));
    } catch (err) {
      Alert.error(t('learning:course_cant_save_changes'));
      throw err;
    }
  };

  const handleChangeModuleStatus = async (id: string, published: boolean) => {
    if (!course) return;
    if (published) {
      const res = await Api.get<{ data: { screens: Screen[] } }>(`/v2/organisations/${organisationId}/modules/${id}`);
      const unpublishedScreen = res.data.screens.find((scr) => !scr.published);
      if (unpublishedScreen) {
        return setModuleCascadePublishId(id);
      }
    }
    await dispatch(updateModule(id, { published }, course.id));
    Alert.success(published
      ? t('learning:course_has_been_published')
      : t('learning:course_has_been_unpublished'));
  };

  const handleDuplicateModule = async (id: string) => {
    try {
      // TODO: no section data available so can't add to state
      const { section } = await dispatch(duplicateModule(id, course.id)) as unknown as { section: SectionType };
      if (section.transparent) { // in the UI the module is not within a section collapsible
        // Find the original module's section index to insert duplicate right after it
        const originalSectionIndex = order.findIndex((orderSection) => orderSection.module_ids.includes(id)
        );

        const newSectionData = R.omit(['modules'], {
          ...section,
          module_ids: R.pluck('id', section.modules),
        });

        // Insert right after the original section (or at the end if not found)
        const insertIndex = originalSectionIndex >= 0 ? originalSectionIndex + 1 : order.length;
        setOrder(R.insert(insertIndex, newSectionData, order));
      } else {
        const newOrder = order.map((orderSection) => {
          if (orderSection.id === section.id) {
            return {
              ...section,
              module_ids: section.modules.map((mod) => mod.id),
            };
          }
          return orderSection;
        });
        setOrder(newOrder);
      }
      Alert.success(t('learning:module_duplicated'));
    } catch (err) {
      Alert.warning(t('learning:warning_duplicate_module'));
      throw err;
    }
  };

  const handleRemoveModule = async (id: string, sectionId: string) => {
    try {
      await dispatch(removeModule(id));
      const updatedOrder = order.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          module_ids: R.reject(R.equals(id), section.module_ids),
        };
      }).filter((section) => {
        // Only remove transparent/local sections that are empty
        // Keep non-transparent sections even if empty (they might be intentionally empty)
        return (!section.transparent && !section.local) || section.module_ids.length > 0;
      });

      // Update state
      setOrder(updatedOrder);

      // Persist the course update to remove module_id from section
      await save(updatedOrder);

      Alert.success(t('learning:module_removed'));
    } catch (err) {
      Alert.warning(t('learning:warning_remove_module'));
      throw err;
    }
  };

  const onCascadeModulePublishConfirm = async (publishScreens: boolean) => {
    if (!course) return;
    try {
      await dispatch(updateModule(moduleCascadePublishId, { published: true }, course.id, publishScreens));
      Alert.success(t('learning:course_has_been_published'));
    } catch (err: any) {
      if (err.status_code === 422) {
        Alert.warning(t('learning:module_active_question_required'));
      } else {
        Alert.error(t('learning:module_update_error'));
      }
      throw err;
    }
    setModuleCascadePublishId(null);
  };

  return (
    <>
      <Bar>
        <h2 className="pull-left">
          <Trans
            i18nKey="learning:course_modules_count"
            values={{ count: Object.keys(lookUp.modules).length }}
          />
        </h2>
        <div className="pull-right">
          <AddModuleForm
            course={course}
            onOpenCourse={handleOpenModule}
          >
            <Button type="primary" iconRight="add" size="large">
              <Trans i18nKey="learning:course_new_module" />
            </Button>
          </AddModuleForm>
        </div>
        <Button
          iconRight="add"
          onClick={handleAddNewSection}
          type="primary"
          size="large"
          disabled={isCreatingSection}
        >
          { t('learning:course_new_section') }
        </Button>
      </Bar>
      <DndProvider manager={dndManager}>
        <ScrollArea position="top" />
        {items.map((section, i) => (
          <Section
            key={section.id}
            item={section}
            previous={items[i - 1]}
            next={items[i + 1]}
            openModule={handleOpenModule}
            moveModule={handleMoveModule}
            onDrop={saveOrder}
            moveModuleToSection={handleMoveModuleToSection}
            moveSection={handleMoveSection}
            changeModuleStatus={handleChangeModuleStatus}
            createNewSection={handleCreateNewSection}
            removeSection={handleRemoveSection}
            removeModule={handleRemoveModule}
            duplicateModule={handleDuplicateModule}
            onNameSave={onSectionNameSave}
            isReordering={isReordering}
          />
        ))}
        <ScrollArea position="bottom" />
      </DndProvider>
      {moduleCascadePublishId && (
        <Confirm
          title={t('learning:form_module_confirm_publish_title')}
          description={t('learning:form_module_confirm_publish_description')}
          check={t('learning:form_module_confirm_publish_check')}
          onConfirm={onCascadeModulePublishConfirm}
          onCancel={() => setModuleCascadePublishId(null)}
        />
      )}
    </>
  );
};

export default CourseContent;
